import moment from "moment";
import * as XLSX from "xlsx";

import {
  ApiApi,
  ApiUploadtasksCreateRequest,
  UploadTaskBroDomainEnum,
  UploadTaskRegistrationTypeEnum,
  UploadTaskRequestTypeEnum,
} from "@nens/brostar/openapi-client";

import { EditGMNMeasuringPointTask, GMNClosureTask, GMNMonitoringPointTask } from "../models/GMN";
import {
  CreateGMNTask,
  CreateGMWEventTask,
  CreateGMWTask,
  CreateGUFAddRealisedInstallationTask,
  CreateGUFClosureRealisedPartTask,
  CreateGUFClosureTask,
  CreateGUFExpandedRealisedInstallationTask,
  CreateGUFHeightTask,
  CreateGUFNewLicenceTask,
  CreateGUFStartRegistrationTask,
  CreateGUFWellFunctionTask,
  DeleteGLDAdditionTask,
  EditFRDMeasurementConfigTask,
  EditFRDMeasurementTask,
  EditGARTask,
  EditGLDAdditionTask,
  EditGLDStartRegistrationTask,
  EditGMNTask,
  EditGMWEventTask,
  EditGMWTask,
  EditGPDAddReportTask,
  EditGPDStartRegistrationTask,
  EditGUFAddRealisedInstallationTask,
  EditGUFClosureRealisedPartTask,
  EditGUFClosureTask,
  EditGUFExpandedRealisedInstallationTask,
  EditGUFHeightTask,
  EditGUFNewLicenceTask,
  EditGUFStartRegistrationTask,
  EditGUFWellFunctionTask,
  FRDClosureTask,
  FRDMeasurementConfigTask,
  FRDMeasurementTask,
  FRDStartRegistrationTask,
  GARTask,
  GLDAdditionTask,
  GLDClosureTask,
  GLDStartRegistrationTask,
  GPDAddReportTask,
  GPDEndRegistrationTask,
  GPDStartRegistrationTask,
  SendEditFRDMeasurementConfigTask,
  SendEditFRDMeasurementTask,
  SendFRDMeasurementConfigTask,
  SendFRDMeasurementTask,
} from "../models/index";

const { Registration, Replace, Delete, Move } = UploadTaskRequestTypeEnum;
const {
  GmnStartRegistration,
  GmwConstruction,
  GldStartRegistration,
  Gar: GarRegistration,
  GldClosure,
  GmnClosure,
  GmnMeasuringPoint,
  FrdStartRegistration,
  FrdGemMeasurement,
  FrdGemMeasurementConfiguration,
  FrdClosure,
  GufStartRegistration,
  GufNewLicense,
  GufAddRealisedInstallation,
  GufExpandedRealisedInstallation,
  GufHeight,
  GufWellFunction,
  GufClosure,
  GufClosureRealisedPart,
  GpdStartRegistration,
  GpdAddReport,
  GpdEndRegistration,
} = UploadTaskRegistrationTypeEnum;
const { Gmn, Gmw, Gld, Frd, Gar: GarDomain, Guf, Gpd } = UploadTaskBroDomainEnum;

export function formatTimestampYYMMDDHHMM(date: Date = new Date()): string {
  return moment(date).format("YYMMDDHHmm");
}

export class UploadApi extends ApiApi {
  // **************
  // GMN
  // **************
  async createGMN(taskInfo: CreateGMNTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.sourcedocumentData.startDateMonitoring}_${taskInfo.sourcedocumentData.name}_GMNRegister`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GmnStartRegistration,
        requestType: Registration,
        broDomain: Gmn,
        projectNumber,
        sourcedocumentData: taskInfo.sourcedocumentData,
        metadata,
      },
    });
  }

  async editGmnMeasuringPoint(taskInfo: EditGMNMeasuringPointTask) {
    // We retrieve the projectnumber, form eventDate and measuringpointcode for inspection
    const projectNumber = taskInfo.metadata.projectNumber;
    const newEventDate = taskInfo.sourcedocumentData.eventDate;
    const measuringPointCode = taskInfo.sourcedocumentData.measuringPointCode;

    // Fetch the current measuring point to compare event dates
    // We use the measuringpointCode to query the measuringpoint addition in the API, and find the original eventDate
    const mpList = await this.apiGmnMeasuringpointsList({ measuringpointCode: measuringPointCode });

    // There will only be one measuringPointAddition for each measuringpointCode
    // Therefore this look-up will work, and it is not required to save the originalDate somewhere.
    // We can continue to retrieve it like this.
    const originalMp = mpList.results.find((mp) => mp.measuringpointCode === measuringPointCode);

    // measuringpointStartDate is stored as Date; convert to YYYY-MM-DD string for comparison
    const originalEventDate = originalMp?.measuringpointStartDate
      ? originalMp.measuringpointStartDate.toISOString().slice(0, 10)
      : undefined;

    // If the eventDate changed between the form and the API value, a MoveRequest is required.
    const eventDateChanged = originalEventDate && originalEventDate !== newEventDate;

    // Format the values for the replaceRequest, use the originalDate, as a changed data
    // is only allowed within a moveRequest, which does not allow for any other changes.
    const replaceSourcedocumentData = eventDateChanged
      ? { ...taskInfo.sourcedocumentData, eventDate: originalEventDate }
      : taskInfo.sourcedocumentData;

    const replaceMetadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${replaceSourcedocumentData.eventDate}_${measuringPointCode}_MP_Correction`,
    };

    const replaceResult = await this.apiUploadtasksCreate({
      data: {
        registrationType: GmnMeasuringPoint,
        requestType: Replace,
        broDomain: Gmn,
        projectNumber,
        metadata: replaceMetadata,
        sourcedocumentData: replaceSourcedocumentData,
      },
    });

    // Once the replace is send, if it is required. We will wait 5 seconds (for the request to be processed)
    // Then we will send a moveRequest
    // This is definitely not fool-proof. But it will work for now and offer a feasable option for users
    // If they are processed within the wrong order, a user could download the files and hand them in manually.
    // It is not the prettiest. But it will work while we design something better.
    // FUTURE: Create an alternative for automatic MoveRequests
    if (eventDateChanged) {
      this.scheduleUploadTaskCreateRequest(5000, {
        data: {
          registrationType: GmnMeasuringPoint,
          requestType: Move,
          broDomain: Gmn,
          projectNumber,
          metadata: {
            ...taskInfo.metadata,
            requestReference: `${newEventDate}_${measuringPointCode}_MP_Move`,
          },
          sourcedocumentData: {
            dateToBeCorrected: originalEventDate,
            ...taskInfo.sourcedocumentData,
          },
        },
      });
    }

    return replaceResult;
  }

  async deleteGmnMeasuringPoint(taskInfo: EditGMNMeasuringPointTask) {
    // These use a bulk upload
    const requestReference = `${taskInfo.sourcedocumentData.eventDate}_${taskInfo.sourcedocumentData.measuringPointCode}_MP_Delete`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;
    const sourcedocumentData = taskInfo.sourcedocumentData;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GmnMeasuringPoint,
        requestType: Delete,
        broDomain: Gmn,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  async createGmnMeasuringPoints({ measuringPointsFile, ...restTask }: GMNMonitoringPointTask) {
    // These use a bulk upload
    return this.apiBulkuploadsCreate({
      ...restTask,
      projectNumber: restTask.metadata.projectNumber,
      measurementTvpFile: measuringPointsFile, // What we call timeseriesFile is called fieldworkFile in the API
      metadata: JSON.stringify({
        ...restTask.metadata,
        requestReference: `${restTask.metadata.broId}_MeasuringPoints_Bulk_BROSTAR_Request`,
      }),
      bulkUploadType: "GMN",
    });
  }

  async createGmnClosure(taskInfo: GMNClosureTask) {
    // These use a bulk upload
    const requestReference = `${taskInfo.sourcedocumentData.endDateMonitoring}_${taskInfo.metadata.broId}_Closure`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    const sourcedocumentData = taskInfo.sourcedocumentData;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GmnClosure,
        requestType: Registration,
        broDomain: Gmn,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  async editGMN(taskInfo: EditGMNTask) {
    const projectNumber = taskInfo.metadata.projectNumber;
    const broId = taskInfo.metadata.broId;
    const newStartDate = taskInfo.sourcedocumentData.startDateMonitoring;

    // Fetch the current GMN to compare start dates
    const gmnList = await this.apiGmnGmnsList({ broId });
    const originalGmn = gmnList.results.find((g) => g.broId === broId);
    // startDateMonitoring is stored as Date; convert to YYYY-MM-DD string for comparison
    const originalStartDate = originalGmn?.startDateMonitoring
      ? originalGmn.startDateMonitoring.toISOString().slice(0, 10)
      : undefined;

    const startDateChanged = originalStartDate !== undefined && originalStartDate !== newStartDate;

    const replaceSourcedocumentData = startDateChanged
      ? { ...taskInfo.sourcedocumentData, startDateMonitoring: originalStartDate }
      : taskInfo.sourcedocumentData;

    const replaceMetadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${replaceSourcedocumentData.startDateMonitoring}_${broId}_Correction`,
    };

    const replaceResult = await this.apiUploadtasksCreate({
      data: {
        registrationType: GmnStartRegistration,
        requestType: Replace,
        broDomain: Gmn,
        broId,
        projectNumber,
        sourcedocumentData: replaceSourcedocumentData,
        metadata: replaceMetadata,
      },
    });

    if (startDateChanged) {
      this.scheduleUploadTaskCreateRequest(5000, {
        data: {
          registrationType: GmnStartRegistration,
          requestType: Move,
          broDomain: Gmn,
          broId,
          projectNumber,
          metadata: {
            ...taskInfo.metadata,
            requestReference: `${newStartDate}_${broId}_Move`,
          },
          sourcedocumentData: {
            dateToBeCorrected: originalStartDate,
            ...taskInfo.sourcedocumentData,
          },
        },
      });
    }

    return replaceResult;
  }

  // **************
  // GMW
  // **************
  async createGMW(taskInfo: CreateGMWTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.sourcedocumentData.wellConstructionDate}_${taskInfo.sourcedocumentData.objectIdAccountableParty}_Registration`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GmwConstruction,
        requestType: Registration,
        projectNumber,
        broDomain: Gmw,
        sourcedocumentData: taskInfo.sourcedocumentData,
        metadata,
      },
    });
  }

  async editGMW(taskInfo: EditGMWTask) {
    const projectNumber = taskInfo.metadata.projectNumber;
    const broId = taskInfo.metadata.broId;
    const newConstructionDate = taskInfo.sourcedocumentData.wellConstructionDate;

    // Fetch the current GMW to compare construction dates
    const gmwList = await this.apiGmwGmwsList({ broId });
    const originalConstructionDate = gmwList.results.find(
      (g) => g.broId === broId
    )?.wellConstructionDate;

    const constructionDateChanged =
      originalConstructionDate && originalConstructionDate !== newConstructionDate;

    // For the replace, use the original construction date so BRO accepts it
    const replaceSourcedocumentData = constructionDateChanged
      ? { ...taskInfo.sourcedocumentData, wellConstructionDate: originalConstructionDate }
      : taskInfo.sourcedocumentData;

    const replaceMetadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${replaceSourcedocumentData.wellConstructionDate}_${broId}_Correction`,
    };

    const replaceResult = await this.apiUploadtasksCreate({
      data: {
        registrationType: GmwConstruction,
        requestType: Replace,
        broDomain: Gmw,
        broId,
        projectNumber,
        metadata: replaceMetadata,
        sourcedocumentData: replaceSourcedocumentData,
      },
    });

    // If construction date changed, schedule a move request after a delay so BRO has time to
    // finish processing the replace before the move arrives. The delay is fire-and-forget so
    // the UI is not blocked.

    // While this could be tracked in the back-end, the other events could not.
    // Therefore we opt to implement them all equally.
    // Explanation of why the other events are not possible in the back-end can be found at EditGMWEvents
    if (constructionDateChanged) {
      this.scheduleUploadTaskCreateRequest(5000, {
        data: {
          registrationType: GmwConstruction,
          requestType: Move,
          broDomain: Gmw,
          broId,
          projectNumber,
          metadata: {
            ...taskInfo.metadata,
            requestReference: `${newConstructionDate}_${broId}_Move`,
          },
          sourcedocumentData: {
            dateToBeCorrected: originalConstructionDate,
            ...taskInfo.sourcedocumentData,
          },
        },
      });
    }

    return replaceResult;
  }

  async createGMWEvent(taskInfo: CreateGMWEventTask) {
    const regType = taskInfo.registrationType.split("_")[1]; // Get the part after "GMW_"
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.sourcedocumentData.eventDate}_${taskInfo.metadata.broId}_${regType}_Registration`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: taskInfo.registrationType,
        requestType: Registration,
        projectNumber,
        broDomain: Gmw,
        sourcedocumentData: taskInfo.sourcedocumentData,
        metadata,
      },
    });
  }

  //
  async editGMWEvent(taskInfo: EditGMWEventTask) {
    const regType = taskInfo.registrationType.split("_")[1]; // Get the part after "GMW_"
    const projectNumber = taskInfo.metadata.projectNumber;
    const broId = taskInfo.metadata.broId;
    const newEventDate = taskInfo.sourcedocumentData.eventDate;

    // Fetch current GMW events to compare event dates
    const eventList = await this.apiGmwEventsList({
      gmwBroId: broId,
      eventName: taskInfo.registrationType,
    });

    // This is probably not gonna find the right match, atleast not always.
    // If there are multiple events with the same name for this broId, this look-up will fail

    const originalEvent = eventList.results.find((e) => e.gmwBroId === broId);
    // eventDate is stored as Date; convert to YYYY-MM-DD string for comparison
    const originalEventDate = originalEvent?.eventDate
      ? originalEvent.eventDate.toISOString().slice(0, 10)
      : undefined;

    const eventDateChanged = originalEventDate !== undefined && originalEventDate !== newEventDate;

    const replaceSourcedocumentData = eventDateChanged
      ? { ...taskInfo.sourcedocumentData, eventDate: originalEventDate }
      : taskInfo.sourcedocumentData;

    const replaceMetadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${replaceSourcedocumentData.eventDate}_${broId}_${regType}_Correction`,
    };

    const replaceResult = await this.apiUploadtasksCreate({
      data: {
        registrationType: taskInfo.registrationType,
        requestType: Replace,
        broDomain: Gmw,
        broId,
        projectNumber,
        metadata: replaceMetadata,
        sourcedocumentData: replaceSourcedocumentData,
      },
    });

    if (eventDateChanged) {
      this.scheduleUploadTaskCreateRequest(5000, {
        data: {
          registrationType: taskInfo.registrationType,
          requestType: Move,
          broDomain: Gmw,
          broId,
          projectNumber,
          metadata: {
            ...taskInfo.metadata,
            requestReference: `${newEventDate}_${broId}_${regType}_Move`,
          },
          sourcedocumentData: {
            dateToBeCorrected: originalEventDate,
            ...taskInfo.sourcedocumentData,
          },
        },
      });
    }

    return replaceResult;
  }

  async deleteGMWEvent(taskInfo: EditGMWEventTask) {
    const regType = taskInfo.registrationType.split("_")[1]; // Get the part after "GMW_"
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.sourcedocumentData.eventDate}_${taskInfo.metadata.broId}_${regType}_Deletion`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;
    const broId = taskInfo.metadata.broId;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: taskInfo.registrationType,
        requestType: Delete,
        broDomain: Gmw,
        broId,
        projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  // **************
  // GAR
  // **************
  async createGar(garInfo: GARTask) {
    // These use a bulk upload
    return this.apiBulkuploadsCreate({
      ...garInfo,
      metadata: JSON.stringify({
        ...garInfo.metadata,
        requestReference: "GAR_Bulk_BROSTAR_Request",
      }),
      bulkUploadType: "GAR",
    });
  }

  async editGar(garInfo: EditGARTask) {
    const tubeNumberPadded = String(
      Number.parseInt(garInfo.sourcedocumentData.tubeNumber)
    ).padStart(3, "0");
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...garInfo.metadata,
      requestReference: `${garInfo.metadata.qualityRegime}_${garInfo.sourcedocumentData.gmwBroId}-${tubeNumberPadded}_Registration`,
    };
    const projectNumber = garInfo.metadata.projectNumber;

    const sourcedocumentData = {
      ...garInfo.sourcedocumentData,
      tubeNumber: Number.parseInt(garInfo.sourcedocumentData.tubeNumber),
    };

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GarRegistration,
        requestType: Registration,
        broDomain: GarDomain,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  // **************
  // GLD
  // **************

  async createGldStartRegistration(taskInfo: GLDStartRegistrationTask) {
    const tubeNumberPadded = String(
      Number.parseInt(taskInfo.sourcedocumentData.tubeNumber)
    ).padStart(3, "0");
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.metadata.qualityRegime}_${taskInfo.sourcedocumentData.gmwBroId}-${tubeNumberPadded}_Registration`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    const sourcedocumentData = {
      ...taskInfo.sourcedocumentData,
      tubeNumber: Number.parseInt(taskInfo.sourcedocumentData.tubeNumber),
    };

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GldStartRegistration,
        requestType: Registration,
        broDomain: Gld,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  async editGldStartRegistration(taskInfo: EditGLDStartRegistrationTask) {
    const tubeNumberPadded = String(
      Number.parseInt(taskInfo.sourcedocumentData.tubeNumber)
    ).padStart(3, "0");
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.metadata.qualityRegime}_${taskInfo.sourcedocumentData.gmwBroId}-${tubeNumberPadded}_Correction`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    const sourcedocumentData = {
      ...taskInfo.sourcedocumentData,
      tubeNumber: Number.parseInt(taskInfo.sourcedocumentData.tubeNumber),
    };

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GldStartRegistration,
        requestType: Replace,
        broDomain: Gld,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  async createGldAddition({ timeseriesFile, ...restTask }: GLDAdditionTask) {
    return this.apiBulkuploadsCreate({
      ...restTask,
      projectNumber: restTask.metadata.projectNumber,
      measurementTvpFile: timeseriesFile, // What we call timeseriesFile is called fieldworkFile in the API
      metadata: JSON.stringify({ ...restTask.metadata, requestReference: "BROSTAR_Request" }),
      sourcedocumentData: JSON.stringify(restTask.sourcedocumentData),
      bulkUploadType: "GLD",
      requestType: "registration",
    });
  }

  async editGldAddition({ timeseriesFile, ...restTask }: EditGLDAdditionTask) {
    return this.apiBulkuploadsCreate({
      ...restTask,
      projectNumber: restTask.metadata.projectNumber,
      measurementTvpFile: timeseriesFile, // What we call timeseriesFile is called fieldworkFile in the API
      metadata: JSON.stringify({ ...restTask.metadata, requestReference: "BROSTAR_Request" }),
      sourcedocumentData: JSON.stringify(restTask.sourcedocumentData),
      bulkUploadType: "GLD",
      requestType: Replace,
    });
  }

  async deleteGldAddition({ ...restTask }: DeleteGLDAdditionTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...restTask.metadata,
      requestReference: `${restTask.sourcedocumentData.observationId}_${restTask.metadata.broId}_Deletion`,
    };
    const projectNumber = restTask.metadata.projectNumber;
    const broId = restTask.metadata.broId;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: "GLD_Addition",
        requestType: Delete,
        broDomain: Gld,
        broId,
        projectNumber,
        metadata,
        sourcedocumentData: restTask.sourcedocumentData,
      },
    });
  }

  async createGldClosure(taskInfo: GLDClosureTask) {
    // These use a bulk upload
    const requestReference = `${taskInfo.metadata.broId}_Closure`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GldClosure,
        requestType: Registration,
        broDomain: Gld,
        projectNumber,
        metadata,
      },
    });
  }

  async deleteGldClosure(taskInfo: GLDClosureTask) {
    // These use a bulk upload
    const requestReference = `${taskInfo.metadata.broId}_Closure_Deletion`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GldClosure,
        requestType: Delete,
        broDomain: Gld,
        projectNumber,
        metadata,
      },
    });
  }

  // **************
  // GPD
  // **************

  async createGpdStartRegistration(taskInfo: GPDStartRegistrationTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.metadata.qualityRegime}_${taskInfo.sourcedocumentData.objectIdAccountableParty}_Registration`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GpdStartRegistration,
        requestType: Registration,
        broDomain: Gpd,
        projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async editGpdStartRegistration(taskInfo: EditGPDStartRegistrationTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.metadata.broId}_Correction`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GpdStartRegistration,
        requestType: Replace,
        broDomain: Gpd,
        projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async createGpdAddReport(taskInfo: GPDAddReportTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.sourcedocumentData.reportId}_${taskInfo.metadata.broId}_Registration`,
    };

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GpdAddReport,
        requestType: Registration,
        broDomain: Gpd,
        projectNumber: taskInfo.metadata.projectNumber,
        broId: taskInfo.metadata.broId,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async editGpdAddReport(taskInfo: EditGPDAddReportTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.sourcedocumentData.reportId}_${taskInfo.metadata.broId}_Correction`,
    };

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GpdAddReport,
        requestType: Replace,
        broDomain: Gpd,
        projectNumber: taskInfo.metadata.projectNumber,
        broId: taskInfo.metadata.broId,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async createGpdEndRegistration(taskInfo: GPDEndRegistrationTask) {
    const requestReference = `${taskInfo.metadata.broId}_EndRegistration`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference,
    };

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GpdEndRegistration,
        requestType: Registration,
        broDomain: Gpd,
        projectNumber: taskInfo.metadata.projectNumber,
        broId: taskInfo.metadata.broId,
        metadata,
        sourcedocumentData: {},
      },
    });
  }

  // **************
  // FRD
  // **************
  async createFrdStartRegistration(taskInfo: FRDStartRegistrationTask) {
    const tubeNumberPadded = String(
      Number.parseInt(taskInfo.sourcedocumentData.tubeNumber)
    ).padStart(3, "0");
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.metadata.qualityRegime}_${taskInfo.sourcedocumentData.gmwBroId}-${tubeNumberPadded}_Registration`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    const sourcedocumentData = {
      ...taskInfo.sourcedocumentData,
      tubeNumber: Number.parseInt(taskInfo.sourcedocumentData.tubeNumber),
    };

    return this.apiUploadtasksCreate({
      data: {
        registrationType: FrdStartRegistration,
        requestType: Registration,
        broDomain: Frd,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  async editFrdStartRegistration(taskInfo: EditGLDStartRegistrationTask) {
    const tubeNumberPadded = String(
      Number.parseInt(taskInfo.sourcedocumentData.tubeNumber)
    ).padStart(3, "0");
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.metadata.qualityRegime}_${taskInfo.sourcedocumentData.gmwBroId}-${tubeNumberPadded}_Correction`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    const sourcedocumentData = {
      ...taskInfo.sourcedocumentData,
      tubeNumber: Number.parseInt(taskInfo.sourcedocumentData.tubeNumber),
    };

    return this.apiUploadtasksCreate({
      data: {
        registrationType: FrdStartRegistration,
        requestType: Replace,
        broDomain: Frd,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  // Schedules a move upload task to be sent after a delay without blocking the caller.
  // The delay allows BRO time to finish processing the preceding replace before the move arrives.

  // This is not completely fool proof. A request might not be triggered if a user closes the page before
  // the scheduled task is sent.
  private scheduleUploadTaskCreateRequest(
    delayMs: number,
    payload: ApiUploadtasksCreateRequest
  ): void {
    setTimeout(() => {
      this.apiUploadtasksCreate(payload).catch((err) =>
        console.error(`Scheduled ${payload.data.requestType} request failed:`, err)
      );
    }, delayMs);
  }

  // Helper method to read Excel file as binary string
  private readExcelFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(event.target.result as string);
        } else {
          reject(new Error("Failed to read file"));
        }
      };
      reader.onerror = () => reject(new Error("File reading failed"));
      reader.readAsBinaryString(file);
    });
  }

  async createFrdMeasurementConfig(taskInfo: FRDMeasurementConfigTask) {
    // These use a bulk upload
    const requestReference = `${taskInfo.metadata.broId}_MeasurementConfig`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    const sourcedocumentData: SendFRDMeasurementConfigTask["sourcedocumentData"] = {
      measurementConfigurations: [],
    };

    // Process the Excel file if provided
    if (taskInfo.measurementConfigFile) {
      try {
        const fileData = await this.readExcelFile(taskInfo.measurementConfigFile as File);
        const workbook = XLSX.read(fileData, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Convert sheet to 2D array (columns 0-8)
        const sheetData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // Map Excel data to measurement configurations
        const measurementConfigurations = sheetData
          .map((row, index) => {
            // Skip header row if it exists
            if (index === 0 && typeof row[0] === "string") return null;

            // Skip empty rows
            if (!row || row.length === 0 || !row[0]) return null;

            return {
              measurementConfigurationId: row[0]?.toString() || `config_${index}`,
              measurementE1CableNumber: parseInt(row[1]) || 0,
              measurementE1ElectrodeNumber: parseInt(row[2]) || 0,
              measurementE2CableNumber: parseInt(row[3]) || 0,
              measurementE2ElectrodeNumber: parseInt(row[4]) || 0,
              currentE1CableNumber: parseInt(row[5]) || 0,
              currentE1ElectrodeNumber: parseInt(row[6]) || 0,
              currentE2CableNumber: parseInt(row[7]) || 0,
              currentE2ElectrodeNumber: parseInt(row[8]) || 0,
            };
          })
          .filter((config) => config !== null);

        // Store the measurement configurations in sourcedocumentData
        sourcedocumentData.measurementConfigurations = measurementConfigurations;
      } catch (error) {
        console.error("Error processing Excel file:", error);
        throw new Error("Failed to process measurement configuration file");
      }
    }

    return this.apiUploadtasksCreate({
      data: {
        registrationType: FrdGemMeasurementConfiguration,
        requestType: Registration,
        broDomain: Frd,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  async editFrdMeasurementConfig(taskInfo: EditFRDMeasurementConfigTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.metadata.broId}_MeasurementConfig_Correction`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    const sourcedocumentData: SendEditFRDMeasurementConfigTask["sourcedocumentData"] = {
      measurementConfigurations: [],
    };

    // Process the Excel file if provided
    if (taskInfo.measurementConfigFile) {
      try {
        const fileData = await this.readExcelFile(taskInfo.measurementConfigFile as File);
        const workbook = XLSX.read(fileData, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Convert sheet to 2D array (columns 0-8)
        const sheetData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // Map Excel data to measurement configurations
        const measurementConfigurations = sheetData
          .map((row, index) => {
            // Skip header row if it exists
            if (index === 0 && typeof row[0] === "string") return null;

            // Skip empty rows
            if (!row || row.length === 0 || !row[0]) return null;

            return {
              measurementConfigurationId: row[0]?.toString() || `config_${index}`,
              measurementE1CableNumber: parseInt(row[1]) || 0,
              measurementE1ElectrodeNumber: parseInt(row[2]) || 0,
              measurementE2CableNumber: parseInt(row[3]) || 0,
              measurementE2ElectrodeNumber: parseInt(row[4]) || 0,
              currentE1CableNumber: parseInt(row[5]) || 0,
              currentE1ElectrodeNumber: parseInt(row[6]) || 0,
              currentE2CableNumber: parseInt(row[7]) || 0,
              currentE2ElectrodeNumber: parseInt(row[8]) || 0,
            };
          })
          .filter((config) => config !== null);

        // Store the measurement configurations in sourcedocumentData
        sourcedocumentData.measurementConfigurations = measurementConfigurations;
      } catch (error) {
        console.error("Error processing Excel file:", error);
        throw new Error("Failed to process measurement configuration file");
      }
    }

    return this.apiUploadtasksCreate({
      data: {
        registrationType: FrdGemMeasurementConfiguration,
        requestType: Replace,
        broDomain: Frd,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  async deleteFrdMeasurementConfig(taskInfo: FRDMeasurementConfigTask) {
    // These use a bulk upload
    const requestReference = `${taskInfo.metadata.broId}_MeasurementConfig_Deletion`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    const sourcedocumentData: SendFRDMeasurementConfigTask["sourcedocumentData"] = {
      measurementConfigurations: [],
    };

    return this.apiUploadtasksCreate({
      data: {
        registrationType: FrdGemMeasurementConfiguration,
        requestType: Delete,
        broDomain: Frd,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  async createFrdMeasurement(taskInfo: FRDMeasurementTask) {
    // These use a bulk upload
    const requestReference = `${taskInfo.metadata.broId}_Measurement`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    const sourcedocumentData: Partial<SendFRDMeasurementTask["sourcedocumentData"]> =
      taskInfo.sourcedocumentData;
    // Process the Excel file if provided
    if (taskInfo.measurementFile) {
      try {
        const fileData = await this.readExcelFile(taskInfo.measurementFile as File);
        const workbook = XLSX.read(fileData, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Convert sheet to 2D array (columns 0-8)
        const sheetData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // Map Excel data to measurements
        const measurements = sheetData
          .map((row, index) => {
            // Skip header row if it exists
            if (index === 0 && typeof row[0] === "string") return null;

            // Skip empty rows
            if (!row || row.length === 0 || !row[0]) return null;

            return {
              configuration: row[0]?.toString() || `config_${index}`,
              value: parseFloat(row[1]) || 0,
              unit: "Ohm",
            };
          })
          .filter((config) => config !== null);

        // Map Excel data to related_calculated_apparent_formation_resistance configurations
        let elementsCount = 0;
        const relatedCalculatedApparentFormationResistanceStrings = sheetData.map((row, index) => {
          // Skip header row if it exists
          if (index === 0 && typeof row[0] === "string") return null;

          // Skip empty rows
          if (!row || row.length === 0 || !row[0]) return null;

          elementsCount++; // Count the number of valid rows
          return (
            String(parseFloat(row[2]) || 0) +
            "," +
            String(parseFloat(row[3]) || 0) +
            "," +
            String(parseInt(row[4]) || "onbekend")
          );
        });
        const relatedCalculatedApparentFormationResistance =
          relatedCalculatedApparentFormationResistanceStrings.join(" ");

        // Store the measurement configurations in sourcedocumentData
        sourcedocumentData.measurements = measurements;
        sourcedocumentData.relatedCalculatedApparentFormationResistance = {
          values: relatedCalculatedApparentFormationResistance,
          elementCount: elementsCount,
        };
      } catch (error) {
        console.error("Error processing Excel file:", error);
        throw new Error("Failed to process measurement configuration file");
      }
    }

    return this.apiUploadtasksCreate({
      data: {
        registrationType: FrdGemMeasurement,
        requestType: Registration,
        broDomain: Frd,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  async editFrdMeasurement(taskInfo: EditFRDMeasurementTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `${taskInfo.metadata.broId}_Measurement_Correction`,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    const sourcedocumentData: Partial<SendEditFRDMeasurementTask["sourcedocumentData"]> = {};

    // Process the Excel file if provided
    if (taskInfo.measurementFile) {
      try {
        const fileData = await this.readExcelFile(taskInfo.measurementFile as File);
        const workbook = XLSX.read(fileData, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Convert sheet to 2D array (columns 0-8)
        const sheetData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // Map Excel data to measurements
        const measurements = sheetData
          .map((row, index) => {
            // Skip header row if it exists
            if (index === 0 && typeof row[0] === "string") return null;

            // Skip empty rows
            if (!row || row.length === 0 || !row[0]) return null;

            return {
              configuration: row[0]?.toString() || `config_${index}`,
              value: parseFloat(row[1]) || 0,
              unit: "Ohm",
            };
          })
          .filter((config) => config !== null);

        // Map Excel data to related_calculated_apparent_formation_resistance configurations
        const elementsCount = sheet["!rows"]?.length ? sheet["!rows"]?.length : 0;

        const relatedCalculatedApparentFormationResistanceStrings = sheetData.map((row, index) => {
          // Skip header row if it exists
          if (index === 0 && typeof row[0] === "string") return null;

          // Skip empty rows
          if (!row || row.length === 0 || !row[0]) return null;

          // vertikalePositie: parseInt(row[2]) || 0
          // berekendeWaarde: parseInt(row[3]) || 0
          // kwaliteitWaarde: parseInt(row[4]) || 'onbekend'
          return (
            String(parseFloat(row[2]) || 0) +
            "," +
            String(parseFloat(row[3]) || 0) +
            "," +
            String(parseInt(row[4]) || "onbekend")
          );
        });
        const relatedCalculatedApparentFormationResistance =
          relatedCalculatedApparentFormationResistanceStrings.join(" ");

        // Store the measurement configurations in sourcedocumentData
        sourcedocumentData.measurements = measurements;
        sourcedocumentData.relatedCalculatedApparentFormationResistance = {
          values: relatedCalculatedApparentFormationResistance,
          elementCount: elementsCount,
        };
      } catch (error) {
        console.error("Error processing Excel file:", error);
        throw new Error("Failed to process measurement configuration file");
      }
    }

    return this.apiUploadtasksCreate({
      data: {
        registrationType: FrdGemMeasurement,
        requestType: Replace,
        broDomain: Frd,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  async deleteFrdMeasurement(taskInfo: FRDMeasurementTask) {
    // These use a bulk upload
    const requestReference = `${taskInfo.metadata.broId}_Measurement_Deletion`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    const sourcedocumentData = taskInfo.sourcedocumentData;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: FrdGemMeasurement,
        requestType: Delete,
        broDomain: Frd,
        projectNumber,
        metadata,
        sourcedocumentData,
      },
    });
  }

  async createFrdClosure(taskInfo: FRDClosureTask) {
    // These use a bulk upload
    const requestReference = `${taskInfo.metadata.broId}_Closure`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: FrdClosure,
        requestType: Registration,
        broDomain: Frd,
        projectNumber,
        metadata,
      },
    });
  }

  async deleteFrdClosure(taskInfo: FRDClosureTask) {
    // These use a bulk upload
    const requestReference = `${taskInfo.metadata.broId}_Closure_Deletion`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: FrdClosure,
        requestType: Delete,
        broDomain: Frd,
        projectNumber,
        metadata,
      },
    });
  }

  // GUF Additions
  async createGUFStartRegistration(taskInfo: CreateGUFStartRegistrationTask) {
    // These use a bulk upload
    const requestReference = `GUF_Start_Registration`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufStartRegistration,
        requestType: Registration,
        broDomain: Guf,
        projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async editGUFStartRegistration(taskInfo: EditGUFStartRegistrationTask) {
    // These use a bulk upload
    const requestReference = `GUF_Start_Registration_Edit`;
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: requestReference,
    };
    const projectNumber = taskInfo.metadata.projectNumber;

    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufStartRegistration,
        requestType: Replace,
        broDomain: Guf,
        projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async createGUFNewLicence(taskInfo: CreateGUFNewLicenceTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_New_Licence`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufNewLicense,
        requestType: Registration,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async editGUFNewLicence(taskInfo: EditGUFNewLicenceTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_New_Licence_Edit`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufNewLicense,
        requestType: Replace,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async createGUFAddRealisedInstallation(taskInfo: CreateGUFAddRealisedInstallationTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Add_Realised_Installation`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufAddRealisedInstallation,
        requestType: Registration,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async editGUFAddRealisedInstallation(taskInfo: EditGUFAddRealisedInstallationTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Add_Realised_Installation_Edit`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufAddRealisedInstallation,
        requestType: Replace,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async createGUFExpandedRealisedInstallation(taskInfo: CreateGUFExpandedRealisedInstallationTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Expanded_Realised_Installation`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufExpandedRealisedInstallation,
        requestType: Registration,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async editGUFExpandedRealisedInstallation(taskInfo: EditGUFExpandedRealisedInstallationTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Expanded_Realised_Installation_Edit`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufExpandedRealisedInstallation,
        requestType: Replace,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async createGUFHeight(taskInfo: CreateGUFHeightTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Height`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufHeight,
        requestType: Registration,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async editGUFHeight(taskInfo: EditGUFHeightTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Height_Edit`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufHeight,
        requestType: Replace,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async createGUFWellFunction(taskInfo: CreateGUFWellFunctionTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Well_Function`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufWellFunction,
        requestType: Registration,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async editGUFWellFunction(taskInfo: EditGUFWellFunctionTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Well_Function_Edit`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufWellFunction,
        requestType: Replace,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async createGUFClosureRealisedPart(taskInfo: CreateGUFClosureRealisedPartTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Closure_Realised_Part`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufClosureRealisedPart,
        requestType: Registration,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async editGUFClosureRealisedPart(taskInfo: EditGUFClosureRealisedPartTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Closure_Realised_Part_Edit`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufClosureRealisedPart,
        requestType: Replace,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async createGUFClosure(taskInfo: CreateGUFClosureTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Closure`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufClosure,
        requestType: Registration,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }

  async editGUFClosure(taskInfo: EditGUFClosureTask) {
    const metadata: ApiUploadtasksCreateRequest["data"]["metadata"] = {
      ...taskInfo.metadata,
      requestReference: `GUF_Closure_Edit`,
    };
    return this.apiUploadtasksCreate({
      data: {
        registrationType: GufClosure,
        requestType: Replace,
        broDomain: Guf,
        projectNumber: taskInfo.metadata.projectNumber,
        metadata,
        sourcedocumentData: taskInfo.sourcedocumentData,
      },
    });
  }
}
